<template>
    <template v-if="page">
        <section
            id="banner"
            class="tw-relative tw-flex tw-min-h-dvh tw-flex-col tw-items-center tw-overflow-hidden tw-bg-cover tw-bg-center tw-bg-no-repeat"
            :style="{
                backgroundImage: `url(${bannerBgImage})`,
            }">
            <div class="io-container tw-flex tw-grow tw-flex-col tw-text-white lg:tw-mt-40">
                <div class="tw-grid tw-grid-cols-1 tw-justify-between max-xl:tw-gap-16 xl:tw-grid-cols-2">
                    <div class="sm:tw-max-w-2xl lg:tw-max-w-4xl">
                        <h6
                            v-if="page.bannerTag"
                            class="tw-w-max tw-rounded-full tw-border tw-border-white tw-px-6 tw-py-3 tw-text-sm lg:tw-text-base">
                            {{ page.bannerTag }}
                        </h6>
                        <div
                            v-if="page.bannerTitle"
                            class="tw-mt-10 tw-text-4xl tw-font-bold md:tw-text-5xl 3xl:tw-text-6xl"
                            v-html="page.bannerTitle"></div>
                        <div
                            v-if="page.bannerButtons?.length"
                            class="tw-mt-10 tw-flex tw-flex-col tw-items-center tw-gap-8 sm:tw-flex-row">
                            <div
                                v-for="btn in page.bannerButtons"
                                :key="btn.id">
                                <v-btn
                                    class="tw-h-12 md:tw-h-14 md:tw-px-7 xl:tw-text-base"
                                    :color="btn.type == 'flat' ? 'primary' : 'white'"
                                    elevation="0"
                                    rounded="pill"
                                    v-bind="urlProps(btn.url)"
                                    :variant="btn.type">
                                    {{ btn.text }}
                                </v-btn>
                            </div>
                        </div>
                    </div>
                    <div class="tw-flex tw-flex-wrap tw-justify-center tw-gap-5 lg:tw-justify-end">
                        <router-link
                            v-for="(btn, i) in bannerRightButtons"
                            :key="i"
                            class="banner-right-button tw-flex tw-aspect-square tw-size-32 tw-flex-col tw-rounded-2xl"
                            :to="btn.url">
                            <img
                                alt="icon"
                                class="tw-mx-auto tw-my-2 tw-h-16 tw-w-16 tw-object-contain tw-object-center"
                                :src="useMedia(btn.icon).small" />
                            <p class="tw-px-3 tw-pb-3 tw-text-center tw-text-[11px] tw-font-extralight">{{ btn.text }}</p>
                        </router-link>
                    </div>
                </div>
                <div class="tw-mt-16 tw-grid tw-grid-cols-1 tw-justify-between max-xl:tw-gap-8 md:tw-grid-cols-2 xl:tw-mt-24">
                    <div class="max-md:tw-order-2 sm:tw-max-w-2xl lg:tw-max-w-4xl">
                        <div
                            v-if="page.recognitions?.length"
                            class="tw-flex tw-gap-6">
                            <a
                                v-for="recognition in page.recognitions"
                                :key="recognition.id"
                                :href="recognition.url"
                                target="_blank">
                                <img
                                    class="tw-w-24 tw-object-contain"
                                    :src="useMedia(recognition.image).small" />
                            </a>
                        </div>
                    </div>
                    <div class="tw-flex tw-flex-col tw-gap-6 max-md:tw-order-1">
                        <router-link
                            v-for="(product, i) in bannerProducts"
                            :key="i"
                            class="project-main-card tw-flex tw-w-full tw-flex-row tw-items-center tw-gap-4 tw-rounded-3xl tw-p-4 sm:tw-w-96"
                            :class="{ 'tw-ml-auto': i % 2 === 0 }"
                            :to="product.url">
                            <img
                                alt=""
                                class="tw-size-20 tw-rounded-xl tw-object-cover"
                                :src="useMedia(product.image).small" />
                            <div class="tw-flex tw-flex-col tw-justify-between tw-gap-1">
                                <h3 class="tw-font-extrabold tw-text-primary">{{ product.title }}</h3>
                                <p class="tw-text-[13px] tw-text-dark">{{ product.text }}</p>
                            </div>
                        </router-link>
                    </div>
                </div>
            </div>
        </section>
        <section class="io-container radial-grad">
            <h2
                v-if="page.contentTitle"
                v-html="page.contentTitle"></h2>
            <HomepageContentSlider
                v-if="page.contentCards?.length"
                :content="page.contentCards" />
        </section>
        <section class="io-container radial-grad rg-right">
            <Tag
                v-if="page.solutionsTag"
                class="tw-mb-8"
                color="primary"
                :text="page.solutionsTag"></Tag>
            <h2
                v-if="page.solutionsTitle"
                v-html="page.solutionsTitle"></h2>
            <HomepageSolutionsSlider
                v-if="page.solutions?.length"
                :content="page.solutions" />
        </section>
        <section class="io-container tw-text-center">
            <Tag
                v-if="page.productsTag"
                class="tw-mx-auto tw-mb-8"
                color="primary"
                :text="page.productsTag"></Tag>
            <h2
                v-if="page.productsTitle"
                v-html="page.productsTitle"></h2>
            <ProductsGrid :products="page.products" />
        </section>
        <section class="io-container tw-text-center">
            <Tag
                v-if="page.clientsTag"
                class="tw-mx-auto tw-mb-8"
                color="primary"
                :text="page.clientsTag"></Tag>
            <h2
                v-if="page.clientsTitle"
                v-html="page.clientsTitle"></h2>
            <PartnersSlider :partners="clients" />
        </section>
        <section
            id="testemonies"
            class="tw-py-10">
            <div class="io-container tw-relative tw-z-[2]">
                <Tag
                    v-if="page.testimonyTag"
                    class="tw-mb-8"
                    color="primary"
                    :text="page.testimonyTag"></Tag>
                <h2
                    v-if="page.testimonyTitle"
                    v-html="page.testimonyTitle"></h2>
                <TestemoniesSlider
                    v-if="testimonies"
                    :content="testimonies" />
            </div>
        </section>
        <section class="io-container radial-grad rg-right tw-text-center">
            <Tag
                v-if="page.newsTag"
                class="tw-mx-auto tw-mb-8"
                color="primary"
                :text="page.newsTag"></Tag>
            <h2
                v-if="page.newsTitle"
                v-html="page.newsTitle"></h2>
            <div class="tw-mt-10 tw-grid tw-grid-cols-1 tw-gap-x-8 tw-gap-y-16 tw-text-left sm:tw-grid-cols-2 lg:tw-grid-cols-3">
                <NewsCard
                    v-for="i in news"
                    :key="i.id"
                    :news="i" />
            </div>
        </section>
    </template>
    <template v-else>
        <!-- loader -->
        <Loader />
    </template>
</template>

<script lang="ts" setup>
    import { useAppStore } from '@/store/app';
    import { useMedia } from '../composables/useMedia';
    import { useDisplay } from 'vuetify';
    import Tag from '@/components/Tag.vue';
    import moment from 'moment';
    import { urlProps } from '@/composables/urlProps';
    import { useI18n } from 'vue-i18n';

    const { t } = useI18n();
    const appStore = useAppStore();
    const $display = useDisplay();

    const page = computed(() => {
        return appStore.getHomepage;
    });

    const bannerBgImage = computed(() => {
        return $display.lgAndUp.value ? useMedia(page.value?.bannerDesktopImage).original : useMedia(page.value?.bannerMobileImage).original;
    });

    const bannerRightButtons = computed(() => {
        return page.value?.bannerRightButtons || [];
    });

    const bannerProducts = computed(() => {
        return page.value?.bannerProducts || [];
    });

    const clients = computed(() => {
        return appStore.getClients;
    });

    const testimonies = computed(() => {
        return appStore.getTestimonies;
    });

    const news = computed(() => {
        return appStore.getNews.slice(0, 3);
    });

    onMounted(() => {
        document.querySelector('main.v-main')?.classList.add('lg:tw-p-0');
    });

    onUnmounted(() => {
        document.querySelector('main.v-main')?.classList.remove('lg:tw-p-0');
    });

    document.title = t('home.docTitle');
</script>

<style scoped lang="scss">
    #testemonies {
        @apply tw-relative tw-bg-blue-650 tw-bg-opacity-15;

        &::before {
            position: absolute;
            inset: 0;
            content: '';
            z-index: 1;
            background: url('@/assets/gradients/gradient-blue-650.png');
            background-position: center center;
            background-size: cover;
            filter: opacity(0.77);
            opacity: 0.16;
        }
    }

    .project-main-card {
        @apply tw-relative tw-z-0;
        background: linear-gradient(90deg, rgba(167, 212, 255, 1) 0%, rgba(180, 218, 255, 1) 30%, rgba(207, 234, 255, 1) 100%);
    }

    .banner-right-button {
        background: radial-gradient(circle at bottom left, rgb(151, 189, 249) 0%, rgb(146, 185, 249) 50%, rgb(135, 180, 247) 100%);
    }
</style>
